import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpParams } from '@angular/common/http';
import { SelectItem, ConfirmationService } from 'primeng/api';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Company } from '../models/company';
import { User, UserStatus } from '../models/user';
import { status as StatusColorMap } from '../models/agency';
import { AiComposerGuideline } from '../models/ai-composer';
import { CompanyService } from '../services/company/company.service';
import { AgencyService } from '../services/agency/agency.service';
import { UserDataManager } from '../user/UserDataManager';
import { MessageHandlerService } from '../services/message-handler.service';
import { ProgressBarService } from '../services/progress-bar.service';
import { AiComposerService } from '../services/ai-composer/ai-composer.service';
import { RoleID } from '../common/Constants';

type AiComposerTab = 'narrative' | 'notes';

@Component({
  selector: 'app-ai-composer',
  templateUrl: './ai-composer.component.html',
  styleUrls: ['./ai-composer.component.scss'],
})
export class AiComposerComponent implements OnInit, OnDestroy {
  statusEnum = StatusColorMap;
  // The question types the LLM service's notes check accepts (IOHB-1880).
  questionTypeOptions: SelectItem[] = [
    { label: 'Subjective', value: 'subjective' },
    { label: 'Assessment', value: 'assessment' },
    { label: 'Therapy', value: 'therapy' },
    { label: 'IDG', value: 'idg' },
  ];
  // Optional note type for a narrative guideline; empty applies to any note type (IOHB-1880).
  noteTypeOptions: SelectItem[] = [{ label: 'IDG', value: 'idg' }];
  currentUser: User;
  loading = false;
  activeTabIndex = 0;
  selectedTab: AiComposerTab = 'narrative';

  searchInput = '';
  selectedFilterCompanyIds: string[] = [];
  selectedFilterAgencyIds: string[] = [];
  selectedFilterVisitCodes: string[] = [];
  selectedFilterDisciplines: string[] = [];

  companyOptions: SelectItem[] = [];
  filteredCompanyOptions: SelectItem[] = [];
  companiesAll: Company[] = [];
  agenciesFilter: SelectItem[] = [];
  agenciesForTableFilter: SelectItem[] = [];
  visitCodeOptions: SelectItem[] = [];
  formVisitCodeOptions: SelectItem[] = [];
  disciplineOptions: SelectItem[] = [];
  loadingVisitTypeMetadata = false;
  promptGuideVisible = false;

  allGuidelines: AiComposerGuideline[] = [];
  narrativeGuidelines: AiComposerGuideline[] = [];
  pagedNarrativeGuidelines: AiComposerGuideline[] = [];
  totalGuidelines = 0;
  allIdgNotes: AiComposerGuideline[] = [];
  idgNotes: AiComposerGuideline[] = [];
  pagedIdgNotes: AiComposerGuideline[] = [];
  totalIdgNotes = 0;
  pageNo = 0;
  noOfRecordsPerRow = 10;
  rowsPerPageOptions: number[] = [10, 20, 30];

  sideFormVisible = false;
  isEditMode = false;
  isSubmitting = false;
  loadingHistory = false;
  loadingAgencies = false;
  selectedGuideline: AiComposerGuideline | null = null;
  selectedGuidelineTab: AiComposerTab = 'narrative';
  guidelineForm: FormGroup;
  agencies: SelectItem[] = [];
  changeHistoryEntries: Array<{ title: string; description: string }> = [];
  historyEmptyMessage = 'No change history found.';

  private subscriptions: Subscription[] = [];
  private searchSubject = new Subject<string>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly aiComposerService: AiComposerService,
    private readonly companyService: CompanyService,
    private readonly agencyService: AgencyService,
    private readonly userDataManager: UserDataManager,
    private readonly messageHandlerService: MessageHandlerService,
    private readonly progressBarService: ProgressBarService,
    private readonly confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.guidelineForm = this.fb.group({
      companyId: ['', Validators.required],
      agencyIds: [[], Validators.required],
      visit_types: [[], Validators.required],
      disciplines: [[], Validators.required],
      templateTitle: ['', [Validators.required, Validators.maxLength(255)]],
      style_guide: ['', [Validators.required, Validators.maxLength(2000)]],
      prompt_template: ['', [Validators.required, Validators.maxLength(5000)]],
      note_type: [''],
      questionType: ['', [Validators.maxLength(2000)]],
      checkingPrompt: ['', [Validators.maxLength(5000)]],
      include_baseline: [false],
      status: [true],
    });

    this.subscriptions.push(
      this.userDataManager.user$.subscribe((user) => {
        if (!user) {
          return;
        }
        this.currentUser = user;
        if (this.isSuperAdminOrIOHAdmin()) {
          this.getCompanies();
          this.loadAgencies();
        }
        this.fetchTemplates();
      }),
    );

    this.subscriptions.push(
      this.searchSubject
        .pipe(debounceTime(500), distinctUntilChanged())
        .subscribe(() => {
          this.fetchTemplates();
        }),
    );

    this.subscriptions.push(
      this.guidelineForm.get('companyId')!.valueChanges.subscribe(() => {
        this.updateAgencyControlState();
      }),
    );
    this.loadVisitTypeMetadata();
    this.applyFormValidatorsForTab(this.selectedTab);
    this.updateAgencyControlState();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  isSuperAdminOrIOHAdmin(): boolean {
    return !!(this.currentUser?.is_super_admin || this.currentUser?.is_IOH_admin);
  }

  setActiveTab(index: number): void {
    this.activeTabIndex = index;
    this.selectedTab = index === 1 ? 'notes' : 'narrative';
    this.applyFormValidatorsForTab(this.selectedTab);
    this.onFilterChange();
    this.fetchTemplates();
  }

  openPromptGuide(): void {
    this.promptGuideVisible = true;
  }

  closePromptGuide(): void {
    this.promptGuideVisible = false;
  }

  private formatHistoryDate(value: any): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return String(value);
    }
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  }

  private getTemplateId(guideline: AiComposerGuideline | null | undefined): string {
    return guideline?.templateId || guideline?._id || guideline?.id || '';
  }

  private normalizeStringArray(value: any): string[] {
    if (Array.isArray(value)) {
      return value
        .map((entry) => {
          if (typeof entry === 'string') {
            return entry.trim();
          }
          if (entry && typeof entry === 'object') {
            return String(entry.value || entry.code || entry.name || entry.label || '').trim();
          }
          return '';
        })
        .filter(Boolean);
    }
    if (typeof value === 'string') {
      return value
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);
    }
    return [];
  }

  private extractHistoryUserName(userValue: any): string {
    if (!userValue) {
      return 'Unknown User';
    }
    return String(userValue).split('(')[0].trim() || 'Unknown User';
  }

  private applyFormValidatorsForTab(tab: AiComposerTab): void {
    const styleGuideControl = this.guidelineForm.get('style_guide');
    const promptTemplateControl = this.guidelineForm.get('prompt_template');
    const questionTypeControl = this.guidelineForm.get('questionType');
    const checkingPromptControl = this.guidelineForm.get('checkingPrompt');
    if (!styleGuideControl || !promptTemplateControl || !questionTypeControl || !checkingPromptControl) {
      return;
    }
    if (tab === 'narrative') {
      styleGuideControl.setValidators([Validators.required, Validators.maxLength(2000)]);
      promptTemplateControl.setValidators([Validators.required, Validators.maxLength(5000)]);
      questionTypeControl.setValidators([Validators.maxLength(2000)]);
      checkingPromptControl.setValidators([Validators.maxLength(5000)]);
    } else {
      styleGuideControl.setValidators([Validators.maxLength(2000)]);
      promptTemplateControl.setValidators([Validators.maxLength(5000)]);
      questionTypeControl.setValidators([Validators.required, Validators.maxLength(2000)]);
      checkingPromptControl.setValidators([Validators.required, Validators.maxLength(5000)]);
    }
    styleGuideControl.updateValueAndValidity({ emitEvent: false });
    promptTemplateControl.updateValueAndValidity({ emitEvent: false });
    questionTypeControl.updateValueAndValidity({ emitEvent: false });
    checkingPromptControl.updateValueAndValidity({ emitEvent: false });
  }

  private loadTemplateHistory(templateId: string, tab: AiComposerTab): void {
    this.loadingHistory = true;
    this.changeHistoryEntries = [];
    this.historyEmptyMessage = 'No change history found.';
    const request$ = tab === 'narrative'
      ? this.aiComposerService.getNarrativeTemplateHistory(templateId)
      : this.aiComposerService.getNotesTemplateHistory(templateId);
    this.subscriptions.push(
      request$.subscribe({
        next: (res) => {
          const history = Array.isArray((res as any)?.history) ? (res as any).history : [];
          this.historyEmptyMessage = (res as any)?.message || 'No change history found.';
          this.changeHistoryEntries = history.map((entry: any) => {
            const timestamp = this.formatHistoryDate(entry?.timestamp || entry?.formattedDate);
            const userName = this.extractHistoryUserName(entry?.user);
            return {
              title: `${timestamp} \u2014 ${userName}`,
              description: entry?.description || entry?.action || '-',
            };
          });
          this.loadingHistory = false;
        },
        error: () => {
          this.loadingHistory = false;
          this.changeHistoryEntries = [];
          this.messageHandlerService.generateToastMessage(
            'error',
            'AI Composer',
            'Unable to load change history',
          );
        },
      }),
    );
  }

  private updateAgencyControlState(): void {
    const agenciesControl = this.guidelineForm?.get('agencyIds');
    if (!agenciesControl) {
      return;
    }
    const shouldDisable = this.isSuperAdminOrIOHAdmin() && !this.companyIdControlValue;
    if (shouldDisable) {
      agenciesControl.disable({ emitEvent: false });
      return;
    }
    agenciesControl.enable({ emitEvent: false });
  }

  getCompanies(): void {
    this.subscriptions.push(
      this.companyService
        .getAllCompanies({ pagination: { pageNumber: 0, pageSize: 10000 } })
        .subscribe((res) => {
          if (!res?.success) {
            return;
          }
          this.companiesAll = res.content?.data || [];
          const activeCompanies = this.companiesAll.filter(
            (company) => company.status === UserStatus.active,
          );
          this.companyOptions = activeCompanies.map((item) => ({
            label: item.name,
            value: item.id,
          }));
        }),
    );
  }

  filterCompanies(event: any): void {
    const query = (event?.query || '').toLowerCase();
    this.filteredCompanyOptions = this.companyOptions.filter((company) =>
      String(company.label).toLowerCase().includes(query),
    );
  }

  onSelectCompany(event: any): void {
    const selected = event?.value?.value || event?.value;
    if (!selected) {
      return;
    }
    this.confirmationService.confirm({
      message: 'Please confirm if you wanted to select this Company',
      header: 'Confirm Company?',
      icon: 'pi pi-cog',
      acceptLabel: 'Confirm',
      rejectLabel: 'Cancel',
      accept: () => {
        this.guidelineForm.patchValue({ agencyIds: [] });
        this.getAgencyListing(selected);
        this.updateAgencyControlState();
      },
      reject: () => {
        this.guidelineForm.patchValue({ companyId: '', agencyIds: [] });
        this.agencies = [];
        this.updateAgencyControlState();
      },
    });
  }

  removeCompany(): void {
    this.confirmationService.confirm({
      message: 'Please confirm if you wanted to delete this Company',
      header: 'Delete Company?',
      icon: 'pi pi-trash',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'btn-delete',
      accept: () => {
        this.guidelineForm.patchValue({ companyId: '', agencyIds: [] });
        this.agencies = [];
        this.updateAgencyControlState();
      },
    });
  }

  showRemoveCompany(): boolean {
    return !!this.guidelineForm?.value?.companyId?.value;
  }

  getCompanySelectedIcon(index: any): string {
    const selectedIndex = this.companiesAll.find((e) => e.id === index?.value);
    return selectedIndex?.logo ?? '';
  }

  loadAgencies(params?: HttpParams): void {
    this.subscriptions.push(
      this.agencyService.getAgencies(params).subscribe((response) => {
        if (!response?.success) {
          return;
        }
        const mapped = (response.content?.data || []).map((agency) => ({
          label: `${agency.name} (${agency.shortName})`,
          value: agency.id,
          companyId: agency.companyId,
        }));
        this.agenciesForTableFilter = mapped;
        this.agenciesFilter = mapped;
      }),
    );
  }

  getAgencyListing(companyId: string): void {
    this.loadingAgencies = true;
    this.agencies = [];
    const params = new HttpParams().set('companyId', companyId);
    this.subscriptions.push(
      this.agencyService.getAgencies(params).subscribe({
        next: (res) => {
          if (res?.success) {
            this.agencies = (res.content?.data || []).map((agency) => ({
              label: `${agency.name} (${agency.shortName})`,
              value: agency.id,
            }));
          } else {
            this.agencies = [];
          }
          this.loadingAgencies = false;
        },
        error: () => {
          this.agencies = [];
          this.loadingAgencies = false;
        },
      }),
    );
  }

  fetchTemplates(): void {
    if (this.selectedTab === 'notes') {
      this.fetchIdgNotes();
      return;
    }
    this.fetchGuidelines();
  }

  fetchGuidelines(): void {
    this.loading = true;
    this.progressBarService.setProgressBarVisibility(true);
    const query = this.searchInput?.trim() || '';
    const params = query ? new HttpParams().set('search', query) : undefined;
    const request$ = this.aiComposerService.getNarrativeTemplates(params);
    this.subscriptions.push(
      request$.subscribe({
        next: (res) => {
          this.allGuidelines = this.mapTemplatesResponse(res);
          this.recomputeNarrativeTableView();
          this.loading = false;
          this.progressBarService.setProgressBarVisibility(false);
        },
        error: (error) => {
          this.loading = false;
          this.progressBarService.setProgressBarVisibility(false);
          this.messageHandlerService.generateToastMessage(
            'error',
            'AI Composer',
            error?.errorMessage || error?.message || 'Failed to load narrative templates',
          );
        },
      }),
    );
  }

  fetchIdgNotes(): void {
    this.loading = true;
    this.progressBarService.setProgressBarVisibility(true);
    const query = this.searchInput?.trim() || '';
    const params = query ? new HttpParams().set('search', query) : undefined;
    const request$ = this.aiComposerService.getNotesTemplates(params);
    this.subscriptions.push(
      request$.subscribe({
        next: (res) => {
          this.allIdgNotes = this.mapTemplatesResponse(res);
          this.recomputeIdgTableView();
          this.loading = false;
          this.progressBarService.setProgressBarVisibility(false);
        },
        error: (error) => {
          this.loading = false;
          this.progressBarService.setProgressBarVisibility(false);
          this.messageHandlerService.generateToastMessage(
            'error',
            'AI Composer',
            error?.errorMessage || error?.message || 'Failed to load notes templates',
          );
        },
      }),
    );
  }

  private mapTemplatesResponse(res: any): AiComposerGuideline[] {
    const raw = Array.isArray(res)
      ? res
      : Array.isArray((res as any)?.data)
      ? (res as any).data
      : Array.isArray(res?.content?.data)
      ? res.content.data
      : Array.isArray(res?.content)
      ? (res.content as any)
      : [];
    return raw.map((item: any) => ({
      ...item,
      _id: item._id || item.id || item.templateId || item.promptTemplateId || '',
      templateId: item._id || item.id || item.templateId || item.promptTemplateId || '',
      companyId: item.companyId || item.company?._id || '',
      agencyIds: Array.isArray(item.agencyIds)
        ? item.agencyIds
        : Array.isArray(item.agencies)
        ? item.agencies.map((agency: any) => agency?._id).filter(Boolean)
        : [],
      visit_types: this.normalizeStringArray(item.visit_types || item.visitTypes || item.visitType),
      templateTitle: item.templateTitle || '',
      style_guide: item.style_guide || '',
      prompt_template: item.prompt_template || '',
      questionType: typeof item.questionType === 'string' ? item.questionType.toLowerCase() : '',
      checkingPrompt: item.checkingPrompt || '',
      include_baseline: !!item.include_baseline,
      disciplines: this.normalizeStringArray(item.disciplines || item.discipline),
      status: item.status !== false,
      companyName: item.companyName || item.company?.name || '',
      agencyNames: Array.isArray(item.agencyNames)
        ? item.agencyNames
        : Array.isArray(item.agencies)
        ? item.agencies.map((agency: any) => agency?.name).filter(Boolean)
        : [],
    }));
  }

  recomputeNarrativeTableView(): void {
    this.narrativeGuidelines = this.allGuidelines.filter((item) => {
      const matchesCompany =
        !this.selectedFilterCompanyIds.length ||
        this.selectedFilterCompanyIds.includes(item.companyId);
      const matchesAgency =
        !this.selectedFilterAgencyIds.length ||
        item.agencyIds?.some((agencyId) => this.selectedFilterAgencyIds.includes(agencyId));
      const matchesVisit =
        !this.selectedFilterVisitCodes.length ||
        item.visit_types?.some((code) => this.selectedFilterVisitCodes.includes(code));
      const matchesDiscipline =
        !this.selectedFilterDisciplines.length ||
        item.disciplines?.some((discipline) => this.selectedFilterDisciplines.includes(discipline));
      return matchesCompany && matchesAgency && matchesVisit && matchesDiscipline;
    });
    this.totalGuidelines = this.narrativeGuidelines.length;
    this.applyPaginationSlice();
  }

  recomputeIdgTableView(): void {
    this.idgNotes = this.allIdgNotes.filter((item) => {
      const matchesCompany =
        !this.selectedFilterCompanyIds.length ||
        this.selectedFilterCompanyIds.includes(item.companyId);
      const matchesAgency =
        !this.selectedFilterAgencyIds.length ||
        item.agencyIds?.some((agencyId) => this.selectedFilterAgencyIds.includes(agencyId));
      const matchesVisit =
        !this.selectedFilterVisitCodes.length ||
        item.visit_types?.some((code) => this.selectedFilterVisitCodes.includes(code));
      const matchesDiscipline =
        !this.selectedFilterDisciplines.length ||
        item.disciplines?.some((discipline) => this.selectedFilterDisciplines.includes(discipline));
      return matchesCompany && matchesAgency && matchesVisit && matchesDiscipline;
    });
    this.totalIdgNotes = this.idgNotes.length;
    this.applyPaginationSlice();
  }

  private loadVisitTypeMetadata(): void {
    this.loadingVisitTypeMetadata = true;
    this.subscriptions.push(
      this.aiComposerService.getVisitTypeMetadata().subscribe({
        next: (res) => {
          const rows = Array.isArray((res as any)?.data)
            ? (res as any).data
            : Array.isArray(res?.content?.data)
            ? res.content.data
            : [];
          const visitCodes = new Set<string>();
          const disciplines = new Set<string>();
          rows.forEach((row: any) => {
            (Array.isArray(row?.visit_types) ? row.visit_types : []).forEach((code: string) => {
              if (code) {
                visitCodes.add(code);
              }
            });
            (Array.isArray(row?.disciplines) ? row.disciplines : []).forEach((discipline: string) => {
              if (discipline) {
                disciplines.add(discipline);
              }
            });
          });
          this.visitCodeOptions = Array.from(visitCodes)
            .sort((a, b) => a.localeCompare(b))
            .map((code) => ({ label: code, value: code }));
          this.formVisitCodeOptions = this.visitCodeOptions;
          this.disciplineOptions = Array.from(disciplines)
            .sort((a, b) => a.localeCompare(b))
            .map((discipline) => ({ label: discipline, value: discipline }));
          this.loadingVisitTypeMetadata = false;
        },
        error: () => {
          this.loadingVisitTypeMetadata = false;
          this.visitCodeOptions = [];
          this.formVisitCodeOptions = [];
          this.disciplineOptions = [];
          this.messageHandlerService.generateToastMessage(
            'error',
            'AI Composer',
            'Unable to load visit code and discipline options',
          );
        },
      }),
    );
  }

  applyPaginationSlice(): void {
    const start = this.pageNo * this.noOfRecordsPerRow;
    const end = start + this.noOfRecordsPerRow;
    if (this.selectedTab === 'notes') {
      this.pagedIdgNotes = this.idgNotes.slice(start, end);
      return;
    }
    this.pagedNarrativeGuidelines = this.narrativeGuidelines.slice(start, end);
  }

  onSearchInputChange(): void {
    this.pageNo = 0;
    this.searchSubject.next(this.searchInput?.trim() || '');
  }

  onFilterChange(): void {
    this.pageNo = 0;
    if (this.selectedTab === 'notes') {
      this.recomputeIdgTableView();
      return;
    }
    this.recomputeNarrativeTableView();
  }

  sortSelectedCompanyValues(): void {
    if (this.selectedFilterCompanyIds?.length) {
      this.selectedFilterCompanyIds.sort((a, b) => a.localeCompare(b));
    }
  }

  sortSelectedAgencyValues(): void {
    if (this.selectedFilterAgencyIds?.length) {
      this.selectedFilterAgencyIds.sort((a, b) => a.localeCompare(b));
    }
  }

  sortSelectedVisitCodeValues(): void {
    if (this.selectedFilterVisitCodes?.length) {
      this.selectedFilterVisitCodes.sort((a, b) => a.localeCompare(b));
    }
  }

  sortSelectedDisciplineValues(): void {
    if (this.selectedFilterDisciplines?.length) {
      this.selectedFilterDisciplines.sort((a, b) => a.localeCompare(b));
    }
  }

  clearSelectedCompanyValues(): void {
    this.selectedFilterCompanyIds = [];
    this.onFilterChange();
  }

  clearSelectedAgencyValues(): void {
    this.selectedFilterAgencyIds = [];
    this.onFilterChange();
  }

  clearSelectedVisitCodeValues(): void {
    this.selectedFilterVisitCodes = [];
    this.onFilterChange();
  }

  clearSelectedDisciplineValues(): void {
    this.selectedFilterDisciplines = [];
    this.onFilterChange();
  }

  clearAllFilters(): void {
    this.pageNo = 0;
    this.selectedFilterCompanyIds = [];
    this.selectedFilterAgencyIds = [];
    this.selectedFilterVisitCodes = [];
    this.selectedFilterDisciplines = [];
    this.searchInput = '';
    this.onFilterChange();
  }

  paginate(event: any): void {
    this.pageNo = event?.page ?? 0;
    this.noOfRecordsPerRow = event?.rows ?? this.noOfRecordsPerRow;
    this.applyPaginationSlice();
  }

  onAddGuidelineClick(): void {
    this.isEditMode = false;
    this.selectedGuideline = null;
    this.selectedGuidelineTab = this.selectedTab;
    this.sideFormVisible = true;
    this.changeHistoryEntries = [];
    this.loadingHistory = false;
    this.historyEmptyMessage = 'No change history found.';
    this.applyFormValidatorsForTab(this.selectedTab);
    this.guidelineForm.reset({
      companyId: '',
      agencyIds: [],
      visit_types: [],
      disciplines: [],
      templateTitle: '',
      style_guide: '',
      prompt_template: '',
      note_type: '',
      questionType: '',
      checkingPrompt: '',
      include_baseline: false,
      status: true,
    });
    this.agencies = [];
    if (!this.isSuperAdminOrIOHAdmin() && this.currentUser?.companyId) {
      this.getAgencyListing(this.currentUser.companyId);
    }
    this.updateAgencyControlState();
  }

  onRowClick(row: AiComposerGuideline, tab: AiComposerTab = this.selectedTab): void {
    this.isEditMode = true;
    this.selectedGuidelineTab = tab;
    this.selectedGuideline = row;
    this.sideFormVisible = true;
    this.applyFormValidatorsForTab(tab);
    const companyValue = this.companyOptions.find((c) => c.value === row.companyId) || {
      label: row.companyName || '',
      value: row.companyId,
    };
    this.guidelineForm.patchValue({
      companyId: companyValue,
      agencyIds: row.agencyIds || [],
      visit_types: row.visit_types || [],
      disciplines: this.normalizeStringArray((row as any).disciplines || (row as any).discipline),
      templateTitle: row.templateTitle || '',
      style_guide: row.style_guide || '',
      prompt_template: row.prompt_template || '',
      note_type: row.note_type || '',
      questionType: row.questionType ? String(row.questionType).toLowerCase() : '',
      checkingPrompt: row.checkingPrompt || '',
      include_baseline: !!row.include_baseline,
      status: row.status !== false,
    });
    if (row.companyId) {
      this.getAgencyListing(row.companyId);
    }
    const templateId = this.getTemplateId(row);
    if (templateId) {
      this.loadTemplateHistory(templateId, tab);
    } else {
      this.changeHistoryEntries = [];
      this.loadingHistory = false;
    }
    this.updateAgencyControlState();
  }

  onSlideBarClose(): void {
    this.sideFormVisible = false;
    this.isEditMode = false;
    this.isSubmitting = false;
    this.loadingHistory = false;
    this.changeHistoryEntries = [];
    this.historyEmptyMessage = 'No change history found.';
    this.selectedGuideline = null;
    this.selectedGuidelineTab = this.selectedTab;
  }

  onSubmit(): void {
    if (this.guidelineForm.invalid) {
      return;
    }
    let companyId = this.guidelineForm.value.companyId?.value || this.guidelineForm.value.companyId;
    if (!this.isSuperAdminOrIOHAdmin()) {
      companyId = this.currentUser?.companyId;
    }

    const rawQuestionType = this.guidelineForm.value.questionType;
    const payload = {
      visit_types: this.guidelineForm.value.visit_types || [],
      disciplines: this.guidelineForm.value.disciplines || [],
      agencyIds: this.guidelineForm.value.agencyIds || [],
      companyId,
      templateTitle: this.guidelineForm.value.templateTitle?.trim(),
      style_guide: this.guidelineForm.value.style_guide?.trim(),
      prompt_template: this.guidelineForm.value.prompt_template?.trim(),
      // Narrative only; null clears a previously saved note type (IOHB-1880).
      note_type:
        this.selectedGuidelineTab === 'notes' ? undefined : this.guidelineForm.value.note_type || null,
      questionType:
        typeof rawQuestionType === 'string' ? rawQuestionType.trim().toLowerCase() : '',
      checkingPrompt: this.guidelineForm.value.checkingPrompt?.trim(),
      include_baseline: !!this.guidelineForm.value.include_baseline,
      status: !!this.guidelineForm.value.status,
    };

    const templateId = this.selectedGuideline?.templateId || this.selectedGuideline?._id || this.selectedGuideline?.id;
    if (this.isEditMode && !templateId) {
      this.messageHandlerService.generateToastMessage(
        'error',
        'AI Composer',
        'Update is unavailable because template ID is missing in API response.',
      );
      return;
    }

    this.isSubmitting = true;
    const isNotesTab = this.selectedGuidelineTab === 'notes';
    const request$ = this.isEditMode && templateId
      ? isNotesTab
        ? this.aiComposerService.updateNotesTemplate(templateId, payload)
        : this.aiComposerService.updateNarrativeTemplate(templateId, payload)
      : isNotesTab
      ? this.aiComposerService.createNotesTemplate(payload)
      : this.aiComposerService.createNarrativeTemplate(payload);

    this.subscriptions.push(
      request$.subscribe({
        next: (res) => {
          this.isSubmitting = false;
          if (res?.success === false) {
            this.messageHandlerService.generateToastMessage(
              'error',
              'AI Composer',
              res?.errorMessage || 'Unable to save guideline',
            );
            return;
          }
          this.messageHandlerService.generateToastMessage(
            'success',
            '',
            this.isEditMode
              ? isNotesTab
                ? 'IDG Note updated successfully'
                : 'Guideline updated successfully'
              : isNotesTab
              ? 'IDG Note created successfully'
              : 'Guideline created successfully',
          );
          this.onSlideBarClose();
          this.fetchTemplates();
        },
        error: (error) => {
          this.isSubmitting = false;
          this.messageHandlerService.generateToastMessage(
            'error',
            'AI Composer',
            error?.errorMessage || error?.message || 'Unable to save guideline',
          );
        },
        complete: () => {
          this.isSubmitting = false;
        },
      }),
    );
  }

  onDeleteClick(event: Event, row: AiComposerGuideline, tab: AiComposerTab = this.selectedTab): void {
    event.stopPropagation();
    const templateId = row.templateId || row._id || row.id;
    if (!templateId) {
      this.messageHandlerService.generateToastMessage(
        'error',
        'AI Composer',
        'Delete is unavailable because template ID is missing in API response.',
      );
      return;
    }
    this.confirmationService.confirm({
      message: `Delete '${row.templateTitle}'? This cannot be undone.`,
      header: tab === 'notes' ? 'Delete IDG Note' : 'Delete Guideline',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.subscriptions.push(
          (tab === 'notes'
            ? this.aiComposerService.deleteNotesTemplate(templateId)
            : this.aiComposerService.deleteNarrativeTemplate(templateId)
          ).subscribe({
            next: (res) => {
              if (res?.success === false) {
                this.messageHandlerService.generateToastMessage(
                  'error',
                  'AI Composer',
                  res?.errorMessage || 'Unable to delete guideline',
                );
                return;
              }
              this.messageHandlerService.generateToastMessage(
                'success',
                '',
                tab === 'notes' ? 'IDG Note deleted successfully' : 'Guideline deleted successfully',
              );
              this.fetchTemplates();
            },
            error: (error) => {
              this.messageHandlerService.generateToastMessage(
                'error',
                'AI Composer',
                error?.errorMessage || error?.message || 'Unable to delete guideline',
              );
            },
          }),
        );
      },
    });
  }

  get companyIdControlValue(): string {
    const company = this.guidelineForm?.value?.companyId;
    return company?.value || company || '';
  }

  /** Header placeholder when agencies list is empty (not shown while loading — see template). */
  get agenciesListboxEmptyMessage(): string {
    if (this.isSuperAdminOrIOHAdmin() && !this.companyIdControlValue) {
      return 'Select a company to load agencies.';
    }
    const hasCompanyContext = this.isSuperAdminOrIOHAdmin()
      ? !!this.companyIdControlValue
      : !!this.currentUser?.companyId;
    if (hasCompanyContext && (!this.agencies || this.agencies.length === 0)) {
      return 'No agency found.';
    }
    return 'No agencies available.';
  }

  /** When there are no options, disable virtual scroll and use auto height so the box stays compact. */
  listboxVirtualScrollEnabled(options: SelectItem[] | null | undefined): boolean {
    return Array.isArray(options) && options.length > 0;
  }

  /** Tall scrollable viewport only when there are items to list. */
  listboxScrollHeight(options: SelectItem[] | null | undefined): string {
    return Array.isArray(options) && options.length > 0 ? '250px' : 'auto';
  }

  listboxSelectAllLinkLabel(
    controlName: 'agencyIds' | 'visit_types' | 'disciplines',
    options: SelectItem[],
  ): string {
    return this.isListboxAllSelected(controlName, options) ? 'Clear All' : 'Select All';
  }

  onListboxSelectAllClick(
    event: Event,
    controlName: 'agencyIds' | 'visit_types' | 'disciplines',
    options: SelectItem[],
  ): void {
    event.preventDefault();
    this.toggleListboxSelectAll(controlName, options);
  }

  private isListboxAllSelected(
    controlName: 'agencyIds' | 'visit_types' | 'disciplines',
    options: SelectItem[],
  ): boolean {
    const control = this.guidelineForm?.get(controlName);
    if (!control || !Array.isArray(options) || !options.length) {
      return false;
    }
    const allValues = options
      .map((o) => o?.value)
      .filter((v) => v !== undefined && v !== null && String(v) !== '');
    if (!allValues.length) {
      return false;
    }
    const current = Array.isArray(control.value) ? (control.value as string[]) : [];
    return allValues.every((v) => current.includes(v as string));
  }

  private toggleListboxSelectAll(
    controlName: 'agencyIds' | 'visit_types' | 'disciplines',
    options: SelectItem[],
  ): void {
    const control = this.guidelineForm?.get(controlName);
    if (!control || control.disabled || !Array.isArray(options) || !options.length) {
      return;
    }
    const allValues = options
      .map((o) => o?.value)
      .filter((v) => v !== undefined && v !== null && String(v) !== '') as string[];
    if (!allValues.length) {
      return;
    }
    const current = Array.isArray(control.value) ? ([...control.value] as string[]) : [];
    const allSelected = allValues.every((v) => current.includes(v));
    control.setValue(allSelected ? [] : [...allValues]);
    control.markAsDirty();
    control.updateValueAndValidity({ emitEvent: true });
  }

  get isEmptyList(): boolean {
    return this.selectedTab === 'notes'
      ? this.allIdgNotes.length === 0
      : this.allGuidelines.length === 0;
  }

  get totalTemplatesCount(): number {
    return this.selectedTab === 'notes' ? this.totalIdgNotes : this.totalGuidelines;
  }

  get hasActiveSearchOrFilters(): boolean {
    return !!(
      this.searchInput?.trim() ||
      this.selectedFilterCompanyIds?.length ||
      this.selectedFilterAgencyIds?.length ||
      this.selectedFilterVisitCodes?.length ||
      this.selectedFilterDisciplines?.length
    );
  }
}

